
import { useState, useEffect } from 'react';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import NavBar from '@/components/NavBar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import CsvUploader from '@/components/outreach/CsvUploader';
import ContactReview from '@/components/outreach/ContactReview';
import ResearchConfig from '@/components/outreach/ResearchConfig';
import ResearchProgress from '@/components/outreach/ResearchProgress';
import ResultsReview from '@/components/outreach/ResultsReview';
import ResultsExport from '@/components/outreach/ResultsExport';

export interface Contact {
  id: string;
  person: string;
  organization: string;
  website: string;
  email?: string;
  title?: string;
  phone?: string;
  linkedin_url?: string;
  research?: string;
  personalizedMessage?: string;
}

export interface ResearchTask {
  contactId: string;
  status: 'pending' | 'researching' | 'completed' | 'error';
  progress: number;
  error?: string;
}

const PersonalizeOutreach = () => {
  const [activeTab, setActiveTab] = useState('upload');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [salesPitch, setSalesPitch] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [researchTasks, setResearchTasks] = useState<ResearchTask[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    const stored = localStorage.getItem('selectedProspects');
    if (stored) {
      try {
        const prospects = JSON.parse(stored);
        const mappedContacts: Contact[] = prospects.map((p: any) => ({
          id: p.id,
          person: p.name,
          organization: p.company,
          website: '', // Apollo doesn't provide this in search
          email: p.email && p.email !== 'email_not_unlocked@domain.com' ? p.email : undefined,
          title: p.title,
          phone: p.phone,
          linkedin_url: p.linkedin_url,
        }));
        setContacts(mappedContacts);
        setActiveTab('review');
        localStorage.removeItem('selectedProspects');
      } catch (error) {
        console.error('Error loading prospects from localStorage:', error);
      }
    }
  }, []);

  const handleCsvUploaded = (uploadedContacts: Contact[]) => {
    setContacts(uploadedContacts);
    setActiveTab('review');
  };

  const handleContactsConfirmed = (confirmedContacts: Contact[]) => {
    setContacts(confirmedContacts);
    setActiveTab('config');
  };

  const handleResearchStarted = (pitch: string, context?: string) => {
    setSalesPitch(pitch);
    setAdditionalContext(context || '');
    const tasks: ResearchTask[] = contacts.map(contact => ({
      contactId: contact.id,
      status: 'pending',
      progress: 0
    }));
    setResearchTasks(tasks);
    setActiveTab('progress');
  };

  const handleSkipConfig = () => {
    // Skip directly to research with default/empty pitch
    const tasks: ResearchTask[] = contacts.map(contact => ({
      contactId: contact.id,
      status: 'pending',
      progress: 0
    }));
    setResearchTasks(tasks);
    setActiveTab('progress');
  };

  const handleResearchCompleted = (updatedContacts: Contact[]) => {
    setContacts(updatedContacts);
    setActiveTab('review-results');
  };

  const handleContinueToExport = () => {
    setActiveTab('export');
  };

  const resetFlow = () => {
    setContacts([]);
    setSalesPitch('');
    setResearchTasks([]);
    setActiveTab('upload');
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 relative bg-gray-50">
          <NavBar onSignOut={() => {}} isAuthenticated={isAuthenticated} />
          <main className="p-8 pt-24">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-4 mb-8">
                {isMobile && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(-1)}
                    className="shrink-0"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                )}
                <div>
                  <h1 className="text-3xl font-bold">Personalize Outreach</h1>
                  <p className="text-gray-600 mt-1">
                    Upload contacts, research prospects, and generate personalized messages
                  </p>
                </div>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-6 mb-8">
                  <TabsTrigger value="upload">Upload</TabsTrigger>
                  <TabsTrigger value="review" disabled={contacts.length === 0}>Review</TabsTrigger>
                  <TabsTrigger value="config" disabled={contacts.length === 0}>Configure</TabsTrigger>
                  <TabsTrigger value="progress" disabled={researchTasks.length === 0}>Research</TabsTrigger>
                  <TabsTrigger value="review-results" disabled={!contacts.some(c => c.research || c.personalizedMessage)}>Review Results</TabsTrigger>
                  <TabsTrigger value="export" disabled={!contacts.some(c => c.personalizedMessage)}>Export</TabsTrigger>
                </TabsList>

                <TabsContent value="upload">
                  <CsvUploader onCsvUploaded={handleCsvUploaded} />
                </TabsContent>

                <TabsContent value="review">
                  <ContactReview 
                    contacts={contacts} 
                    onContactsConfirmed={handleContactsConfirmed}
                  />
                </TabsContent>

                <TabsContent value="config">
                  <ResearchConfig 
                    contacts={contacts}
                    onResearchStarted={handleResearchStarted}
                    onSkip={handleSkipConfig}
                  />
                </TabsContent>

                <TabsContent value="progress">
                  <ResearchProgress 
                    contacts={contacts}
                    salesPitch={salesPitch}
                    additionalContext={additionalContext}
                    researchTasks={researchTasks}
                    setResearchTasks={setResearchTasks}
                    onResearchCompleted={handleResearchCompleted}
                  />
                </TabsContent>

                <TabsContent value="review-results">
                  <ResultsReview 
                    contacts={contacts}
                    onContinueToExport={handleContinueToExport}
                  />
                </TabsContent>

                <TabsContent value="export">
                  <ResultsExport 
                    contacts={contacts}
                    onReset={resetFlow}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default PersonalizeOutreach;
